import React from "react"
import { connect } from "react-redux"
import { compact, values, debounce, chain } from "lodash"
import DocumentTitle from "react-document-title"
import ReactMarkdown from "react-markdown"
import { browserHistory } from "react-router"
import { Glyphicon, Modal, Table, ButtonGroup } from "react-bootstrap"

import AdminNav from "../components/AdminNav"
import { docListStyle } from "../containers/PageStyles"
import FilesSelector from "../lib/FilesSelector"
import { createCity, updateCity } from "../ducks/cities"
import { allCountries as Countries } from "../../lib/countries"
import Button from "../components/Button"
import { getText, translateCity } from "../lib/text"
import { fetchGeolocation, replaceQuery } from "../lib/AppTools"
import { getQuery, getFromQuery2 } from "../selectors"

import CreateCityForm from "./CreateCityForm"
import ModalCityEditor from "./ModalCityEditor"

/**
 * Auxiliary components
 */

const DayBackgroundPreview = ({ dayBg }: { dayBg: string }) => (
  dayBg ? (
    <a href={dayBg} target="_blank" rel="noopener noreferrer">
      <img
        src={dayBg}
        style={{ height: 40, margin: 5 }}
        alt="day background"
      />
    </a>
  ) : (
    <div style={{ height: 40, width: 60, borderColor: "black", borderWidth: 2, margin: 10, padding: 8, display: "inline-block" }}>
      <Glyphicon glyph="remove" />
    </div>
  )
)

const DescriptionPDFPreview = ({ descriptionPDF }: { descriptionPDF: string }) => (
  descriptionPDF
    ? (
      <div style={{ height: 40, width: 60, borderColor: "black", borderWidth: 2, margin: 10, padding: 8, display: "inline-block" }}>
        <a
          href={descriptionPDF}
          target="_blank"
          rel="noreferrer noopener"
          style={{ height: 40, width: 40 }}
        >
          <Glyphicon glyph="file" style={{ height: 40, width: 40 }} />
        </a>
      </div>
    )
    : (
      <div style={{ height: 40, width: 60, borderColor: "black", borderWidth: 2, margin: 10, padding: 8, display: "inline-block" }}>
        <Glyphicon glyph="remove" />
      </div>
    )
)

type FFProps = {
  city: Object,
  onChange: Object => void,
  onClose: () => void
}

const FilesForm = ({ city, onChange, onClose }: FFProps) => {
  console.log({ city, onChange, onClose })

  return (
    <Modal show onHide={() => onClose()}>
      <Modal.Header>
        Ficheros para: {translateCity(city)}
      </Modal.Header>
      <Modal.Body>
        <FilesSelector
          title="Imágenes de la ciudad"
          includeNotes
          value={city.files}
          fileNotes={city.fileNotes}
          onChange={files => onChange({ files })}
          onNotesChange={fileNotes => onChange({ fileNotes })}
          labels={{
            dayBg: "Fondo para documento 1",
            descriptionPDF: "PDF descripción"
          }}
          extensions={{
            dayBg: ["jpg", "jpeg", "png"],
            descriptionPDF: ["pdf"]
          }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={() => onClose()}>{getText("Cerrar")}</Button>
      </Modal.Footer>
    </Modal>
  )
}

/**
 *
 */

type CitiesPageProps = {
  cities: Object,
  update: (string, Object) => void,
  create: (string) => void,
}

function fetchAllDirections(geoLocationData) {
  return Promise.all(
    geoLocationData.map(({ key, address }) => new Promise(resolve => {
      fetchGeolocation(address)
        .then(geolocation => {
          resolve({
            key,
            address,
            preciseLocation: {
              address: geolocation.formatted_address,
              longitude: geolocation.geometry.location.lng,
              latitude: geolocation.geometry.location.lat,
              queryAddress : address
            }
          })
        })
        .catch(e => {
          console.error(`error retrieving (key ${key}) "${address}"`)
          console.error(e)
          resolve({ key, address, preciseLocation: null })
        })
    }))
  )
}

class CitiesPage extends React.PureComponent
{
  constructor(props) {
    super(props)
    this.debounceGeolocation = debounce(() => this.updateGeolocations(this.getSelectedCity()), 500)
  }

  getSelectedCity() {
    return this.props.cities[this.props.editingId]
  }

  getAddresses() {
    const selectedCity = this.getSelectedCity()
    const stations = selectedCity.stations

    const geoLocationData = []

    const cityQueryAddress = encodeURIComponent(`${selectedCity.name}, ${selectedCity.country}`)
    if (cityQueryAddress != selectedCity.preciseLocation.queryAddress) {
      geoLocationData.push({key: null, address: cityQueryAddress})
    }

    values(stations).forEach(station => {
      const stationQueryAddress = encodeURIComponent(`${station.address}, ${selectedCity.name}, ${selectedCity.country}`)
      if (!station.preciseLocation || stationQueryAddress != station.preciseLocation.queryAddress) {
        geoLocationData.push({key: station.key, address: stationQueryAddress})
      }
    })

    return geoLocationData
  }

  async updateGeolocations() {
    const city = this.getSelectedCity()

    if (!city) return null

    const geoLocationData = this.getAddresses()

    const fetchedDirections = await fetchAllDirections(geoLocationData)

    const { stations } = city
    let changes = { stations }

    fetchedDirections.forEach(({ key, preciseLocation }) => {
      if (key) {
        changes = {
          ...changes,
          stations : {
            ...changes.stations,
            [key] : { ...stations[key], preciseLocation }
          }
        }
      }
      else {
        changes = { ...changes, preciseLocation }
      }
    })

    this.props.update(this.props.editingId, changes)
  }

  props : CitiesPageProps

  render() {
    const { cities, update, create } = this.props
    const citiesList = chain(cities).values().filter().sortBy(c => `${c.country}_${c.name}`).value()
    const selectedCity = this.getSelectedCity()

    return (
      <div style={docListStyle()}>
        <DocumentTitle title={`Tripmaker ${getText("Ciudades")}`} />
        <AdminNav active="cities" />

        <Button
          onClick={() => replaceQuery(browserHistory, this.props.query, { modal: "create" })}
          style={{ marginRight: 5, marginTop: 5, marginBottom: 5 }}
        >
          <Glyphicon glyph="plus" /> {getText("Nueva ciudad")}
        </Button>

        {
          this.props.modal == "create" && (
            <CreateCityForm
              onClose={() => replaceQuery(browserHistory, this.props.query, { modal: null })}
              onCreate={name => {
                create(name, id => replaceQuery(browserHistory, this.props.query, { modal: "edit", editingId: id }))
              }}
            />
          )
        }

        {
          this.props.modal == "edit" && selectedCity && (
            <ModalCityEditor
              city={selectedCity}
              onChange={changes => {
                update(this.props.editingId, changes)
                this.debounceGeolocation()
              }}
              onClose={() => replaceQuery(browserHistory, this.props.query, { modal: null, editingId: null }) }
            />
          )
        }

        {
          this.props.modal == "files" && selectedCity && (
            <FilesForm
              city={selectedCity}
              onChange={changes => update(this.props.editingId, changes)}
              onClose={() => replaceQuery(browserHistory, this.props.query, { modal: null })}
            />
          )
        }

        <Table striped condensed bordered style={{ maxWidth: 950 }}>
          <thead>
            <tr>
              {
                ["", "Ciudad / País", "Nota", "Fondo", "PDF descripción"].map(x => (
                  <th key={x} style={{ textAlign: "center", verticalAlign: "middle" }}>
                    {getText(x)}
                  </th>
                ))
              }
            </tr>
          </thead>
          <tbody>
            {
              compact(citiesList).map(city => (
                <tr key={city._id}>
                  <td style={{ padding:15, minWidth: 100 }}>
                    <ButtonGroup>
                      <Button
                        bsSize="small"
                        tooltipText={getText("Editar ciudad")}
                        onClick={() => replaceQuery(browserHistory, this.props.query, {
                          modal: "edit", editingId: city._id
                        })}
                      >
                        <Glyphicon glyph="pencil" />
                      </Button>

                      <Button
                        bsSize="small"
                        tooltipText={getText("Subir ficheros")}
                        onClick={() => replaceQuery(browserHistory, this.props.query, {
                          modal: "files", editingId: city._id
                        })}
                      >
                        <Glyphicon glyph="upload" />
                      </Button>
                    </ButtonGroup>
                  </td>
                  <td style={{ padding:15 }}>
                    <div><b>{city.name} - {city.chinese}</b></div>
                    <div>{Countries[city.country].flag} {getText(Countries[city.country].name)}</div>
                  </td>
                  <td>
                    <ReactMarkdown source={city.notes || ""} />
                  </td>
                  <td style={{ padding:10, textAlign: "center", verticalAlign: "middle", borderRightWidth: 0, borderRight: "none" }}>
                    <DayBackgroundPreview dayBg={city.files.dayBg} />
                  </td>
                  <td style={{ padding:10, textAlign: "center", verticalAlign: "middle" }}>
                    <DescriptionPDFPreview descriptionPDF={city.files.descriptionPDF} />
                  </td>
                </tr>
              ))
            }
          </tbody>
        </Table>
      </div>
    )
  }
}

const mapStateToProps = (state, props) => ({
  cities: state.cities,
  query: getQuery(state, props),
  editingId: decodeURIComponent(getFromQuery2("editingId")(state, props)) || null,
  modal: getFromQuery2("modal")(state, props) || null,
})

const mapDispatchToProps = dispatch => ({
  update: (id, changes) => { dispatch(updateCity(id, changes)) },
  create: (name, idCallback) => { dispatch(createCity({ name }, idCallback)) }
})

export default connect(mapStateToProps, mapDispatchToProps)(CitiesPage)
